import { useEffect, useRef, useState } from 'react';
import { pitchSpecHz, type Drone, type TuningContext } from '@tonist/shared';
import type { VoiceHandle } from '../../engine/types';
import { ensureEngineStarted, type EngineWithExtras } from '../../lib/engineBoot';
import { degreeLabel } from '../../music/tuning';
import { toast } from '../../store/uiStore';

/** Eight pads (the scale degrees, octave 0) that audition the drone being edited through the real engine. */
export function AuditionStrip({ engine, drone, tuning, ready }: { engine: EngineWithExtras; drone: Drone | null; tuning: TuningContext; ready: boolean }) {
  const n = tuning.scale.semitones.length;
  const voices = useRef(new Map<number, VoiceHandle>());
  const [on, setOn] = useState<Set<number>>(new Set());
  useEffect(() => () => { for (const v of voices.current.values()) v.release(); voices.current.clear(); }, []);

  const down = (degree: number, e: React.PointerEvent<HTMLButtonElement>) => {
    if (!drone) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    ensureEngineStarted(engine).then(() => {
      const hz = pitchSpecHz(tuning, { degree, octave: 0, bendCents: 0 });
      const h = engine.noteOn(drone.id, hz, { radius: 0.6, velocity: 0 });
      voices.current.set(e.pointerId, h);
      setOn((s) => new Set(s).add(degree));
    }).catch(() => toast('Audio could not start', 'error'));
  };
  const up = (degree: number, e: React.PointerEvent<HTMLButtonElement>) => {
    const h = voices.current.get(e.pointerId);
    if (h) { h.release(); voices.current.delete(e.pointerId); }
    setOn((s) => { const next = new Set(s); next.delete(degree); return next; });
  };

  return (
    <div className="stack">
      <div className="eyebrow">Audition {drone ? `— ${drone.name}` : ''}{!ready && drone ? ' (preparing…)' : ''}</div>
      <div className="pads" role="group" aria-label="Audition pads">
        {Array.from({ length: n + 1 }, (_, d) => {
          const l = degreeLabel(tuning.scale, tuning.tonicPc, d, 0);
          return (
            <button key={d} type="button" className={`pad${on.has(d) ? ' is-on' : ''}`} aria-label={`${l.sargam}, ${l.letter}`} disabled={!drone}
              onPointerDown={(e) => down(d, e)} onPointerUp={(e) => up(d, e)} onPointerCancel={(e) => up(d, e)}
              onKeyDown={(e) => { if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); if (!on.has(d) && drone) { ensureEngineStarted(engine).then(() => { const h = engine.noteOn(drone.id, pitchSpecHz(tuning, { degree: d, octave: 0, bendCents: 0 }), { radius: 0.6, velocity: 0 }); setTimeout(() => h.release(), 900); }).catch(() => undefined); } } }}>
              <span>{l.sargam}<small>{l.letter}</small></span>
            </button>
          );
        })}
      </div>
      <div className="hint">Hold a pad to hear the drone at that degree; changes you make apply live.</div>
    </div>
  );
}
